import { MdWarning } from 'react-icons/md';
import { Plover } from '@blisspointmedia/bpm-types/dist/webTrafficOptimization';
import { AlertDialog } from '@Components/AlertDialog';
import { Intent } from '@Components/designConstants';
import { useSetError } from '../../../redux/modals';
import { WtoLambdaFetch } from '../../../utils/fetch-utils';
import { useCompanyInfo } from '../../../utils/hooks/useCompanyInfo';
import { WtoTab, WtoTabType } from '../web-traffic-optimization-constants';
import { WtoRule } from '../wto-form-context';
import { formatDefaultId, formatRules, isSavedDraft, trimBackslashes } from '../wto-utils';

interface ActivateModalProps {
  show: boolean;
  closeModal: () => void;
  data: Plover;
  userEmail: string;
  fetchPlovers: () => void;
  action: string;
  goToTab: (tab: WtoTabType) => void;
}

export const ActivateModal = ({
  show,
  closeModal,
  data,
  userEmail,
  fetchPlovers,
  action,
  goToTab
}: ActivateModalProps) => {
  const { cid } = useCompanyInfo();
  const setError = useSetError();

  const confirmActivation = async () => {
    if (data.rules.length < 1) {
      setError({
        title: 'Activation Error',
        message:
          'No rules detected for this test; at least 1 rule is required for a test to go live'
      });
      return;
    }
    try {
      if (action.toLocaleLowerCase() === 'activate route') {
        await WtoLambdaFetch(`/plover/${data.id}`, {
          method: 'PATCH',
          body: {
            status: 'production'
          },
          params: {
            email: userEmail
          }
        });
      } else if (action.toLocaleLowerCase() === 'update route') {
        await WtoLambdaFetch(`/plover/${data.id}`, {
          method: 'PATCH',
          body: {
            // Plover.rules is only weakly typed (Record<string, any>[]) in bpm-types; every
            // rule read off data is actually WtoRule-shaped at runtime.
            rules: formatRules(data.rules as WtoRule[])
          },
          params: {
            email: userEmail
          }
        });
      } else if (action.toLocaleLowerCase() === 'create route') {
        await WtoLambdaFetch(`/plover${isSavedDraft(data) ? `/${data.id}` : ''}`, {
          method: isSavedDraft(data) ? 'PATCH' : 'POST',
          body: {
            company: cid,
            test_name: data.test_name,
            utm_test_id: data.utm_test_id,
            default_url: data.default_url,
            default_id: formatDefaultId(data.default_id),
            plover_url: data.plover_url,
            folder_path: trimBackslashes(data.folder_path),
            notes: data.notes,
            variants: data.variants,
            domain: data.domain,
            // Plover.rules is only weakly typed (Record<string, any>[]) in bpm-types; every
            // rule read off data is actually WtoRule-shaped at runtime.
            rules: formatRules(data.rules as WtoRule[]),
            status: 'production'
          },
          params: {
            email: userEmail
          }
        });
      }
      fetchPlovers();
      goToTab(WtoTab.ROUTE_TABLE);
    } catch (e) {
      setError({
        message: `Failed to activate route. Error: ${e.message}`,
        reportError: e
      });
    }
  };

  return (
    <AlertDialog
      show={show}
      onHide={closeModal}
      intent={Intent.WARNING}
      icon={<MdWarning size={24} />}
      title={action}
      body={
        <>
          Are you sure you want to{' '}
          {action.toLocaleLowerCase() === 'update route' ? 'update' : 'launch'} the following route:{' '}
          <span>{data.test_name}</span>?
          <br />
          <br />
          <i>
            {action.toLocaleLowerCase() === 'update route'
              ? '(Changes will be live after deployment completes.)'
              : 'IMPORTANT: Please make sure that no LIVE routes are using the same Route URL.'}
          </i>
        </>
      }
      primaryAction={{ onClick: confirmActivation, label: 'Confirm' }}
      secondaryAction={{ onClick: closeModal }}
      passthroughProps={{ 'data-testid': 'activate-modal' }}
    />
  );
};
